import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  ClipboardList,
  Cookie,
  Users,
  AlertTriangle,
  FileSearch,
  LayoutDashboard,
  FileCheck2,
  Building2,
  Clock,
  Lock,
  Sparkles,
  ExternalLink,
  Newspaper,
  History,
  MessageCircle,
  Package,
  Eye,
  Check,
  Minus,
  ChevronDown,
} from 'lucide-react';
import { LogoFull, LogoMark } from '../components/Logo';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/constants';

const MODULES = [
  { icon: ClipboardList, title: 'Diagnóstico gratis', desc: 'Score 0–100 en 15 minutos que muestra dónde está su empresa en cumplimiento.' },
  { icon: LayoutDashboard, title: 'Dashboard SLA', desc: 'Widget de plazos legales por vencer: brechas, ARCO+ y tareas críticas.' },
  { icon: FileCheck2, title: 'RAT + Documentos', desc: 'Registro de tratamientos y políticas generadas con IA (Claude Sonnet).' },
  { icon: Cookie, title: 'Cookies (CMP)', desc: 'Banner embebible + evidencia auditable con hash de IP por consentimiento.' },
  { icon: Users, title: 'Portal ARCO+ público', desc: 'Titulares envían solicitudes con SLA 15 días hábiles. Slug binario anti-abuso.' },
  { icon: AlertTriangle, title: 'Brechas 72 h', desc: '6 etapas · ~25 tareas legales · plantillas de notificación al Art. 25.' },
  { icon: FileSearch, title: 'DPIA · Art. 15 bis', desc: 'Detección automática de alto riesgo · matriz 5×5 · firma del DPO.' },
  { icon: Package, title: 'Carpeta de Fiscalización', desc: 'ZIP con SHA-256 y hashes por archivo, listo para la Agencia.' },
  { icon: History, title: 'Trazabilidad', desc: 'Bitácora automática + eventos manuales importables por CSV.' },
  { icon: Eye, title: 'Rol Auditor Externo', desc: 'Acceso temporal de solo lectura con caducidad configurable.' },
  { icon: MessageCircle, title: 'Copiloto IA', desc: 'Asistente experto en Ley 21.719 y en la navegación del sistema.' },
  { icon: ShieldCheck, title: 'White-label', desc: 'CNAME propio, branding total y emails desde su dominio. (Enterprise)' },
];

const PILLARS = [
  {
    icon: Sparkles,
    title: 'Sin lenguaje jurídico',
    desc: 'Wizards guiados para gerentes de operaciones, RRHH y TI. Cero abogados requeridos para partir.',
  },
  {
    icon: Clock,
    title: '10–15 minutos para partir',
    desc: 'Diagnóstico inicial rápido con score comparable en el tiempo y evolución histórica.',
  },
  {
    icon: Lock,
    title: 'Multi-tenant desde el día 1',
    desc: 'Sus datos aislados por organización. Bitácora inmutable. Preparado para white-label.',
  },
  {
    icon: Building2,
    title: 'Plantillas por industria',
    desc: 'Retail, salud, fintech, educación, e-commerce, logística, servicios profesionales y más.',
  },
];

const STEPS = [
  { n: '01', t: 'Cree su organización', d: 'Razón social, RUT, giro, industria y tamaño. Gratis, sin tarjeta.' },
  { n: '02', t: 'Responda 15 preguntas', d: '8 categorías: gobernanza, RAT, cookies, ARCO+, brechas, seguridad, DPIA, documentos.' },
  { n: '03', t: 'Reciba su score gratis', d: 'Puntaje 0–100 con el nivel de cumplimiento de su empresa por categoría.' },
  { n: '04', t: 'Genere evidencia', d: 'Bitácora inmutable + ZIP con hash SHA-256 listo para fiscalizaciones.' },
];

const TESTIMONIALS = [
  {
    quote: 'Antes íbamos a reuniones con la asesora legal con dudas básicas. Ahora llegamos con el score y las tareas priorizadas ya andando; la reunión rinde 3× más.',
    name: 'Camila Ruiz-Tagle',
    role: 'Gerente de Operaciones',
    org: 'Cordillera Retail',
    sector: 'Retail · 180 personas',
  },
  {
    quote: 'El módulo de brechas 72h nos salvó cuando nos robaron un pendrive con datos de clientes. Las 6 etapas y las plantillas al Art. 25 estaban listas: notificamos a la Agencia en 40 horas.',
    name: 'Rodrigo Fuentes',
    role: 'CTO / DPO informal',
    org: 'Alerce Fintech',
    sector: 'Fintech · 65 personas',
  },
  {
    quote: 'Tenemos 8 clientes bajo administración. Con white-label cada uno ve su propio Priviax con nuestro branding. Cerramos 3 contratos nuevos gracias a la carpeta de fiscalización.',
    name: 'María José Contreras',
    role: 'Socia principal',
    org: 'Contreras & Asociados',
    sector: 'Estudio jurídico · Enterprise',
  },
];

const PLANS = [
  {
    key: 'pyme',
    name: 'PyME',
    tagline: 'Cumplimiento completo para empresas medianas.',
    size: 'Empresas medianas',
    price: '2 UF',
    per: '+ IVA · por mes  ·  UF a valor del día del cobro',
    featured: true,
    badge: 'Más contratado',
    cta: 'Contratar ahora',
    features: [
      'Los 12 módulos completos de la Ley 21.719',
      'Hasta 1 millón de titulares en BD',
      'Hasta 50 actividades RAT',
      '5 usuarios · 1 dominio CMP',
      'DPIA con firma DPO · Fiscalización ZIP · Auditor Externo',
      'Copiloto IA · 250 mensajes/mes',
      'SLA soporte 48h hábiles',
    ],
    excluded: [],
  },
  {
    key: 'empresas',
    name: 'Empresas',
    tagline: 'Para corporativos medianos y grandes que ya están en el radar.',
    size: 'Corporativos medianos y grandes',
    price: '6 UF',
    per: '+ IVA · por mes  ·  UF a valor del día del cobro',
    cta: 'Contratar ahora',
    features: [
      'Todo lo de PyME · sin recortes',
      'Hasta 10 millones de titulares en BD',
      'Hasta 300 actividades RAT',
      '20 usuarios · 5 dominios CMP',
      '30 DPIA / 50 brechas al año',
      'Copiloto IA · 1.500 mensajes/mes',
      'SLA soporte 8h hábiles',
      'Emails desde su dominio (Resend)',
      'Onboarding asistido · 2 sesiones',
    ],
    excluded: [],
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    tagline: 'Holdings, banca y retail nacional. Cotización por volumen.',
    size: 'Holdings · banca · retail nacional',
    price: 'A cotización',
    per: 'Anual · según volumen de datos',
    cta: 'Contactar ventas',
    features: [
      'Todo lo de Empresas · sin topes',
      'Volumen ilimitado de titulares y RATs',
      'Usuarios y dominios CMP ilimitados',
      'White-label completo · CNAME + branding',
      'Multi-tenant padre/hijo · gestione N filiales',
      'SSO / SAML · Active Directory · Okta',
      'Success Manager dedicado',
      'SLA 4h · DPA revisado por su legal',
      'Facturación anual con descuento',
    ],
    excluded: [],
  },
];

const FAQS = [
  {
    q: '¿Qué pasa el 1 de diciembre de 2026?',
    a: 'Entra en vigencia plena la Ley N.º 21.719. Desde esa fecha la Agencia de Protección de Datos puede fiscalizar y aplicar multas: infracciones leves hasta 5.000 UTM, graves hasta 10.000 UTM y gravísimas hasta 20.000 UTM o 4% de los ingresos anuales, lo que sea mayor.',
  },
  {
    q: '¿Puedo empezar sin pagar?',
    a: 'Sí. Puede crear su organización y completar el diagnóstico inicial para obtener su score 0–100 de cumplimiento de forma gratuita. Para acceder al plan de acción priorizado y a los módulos operativos (RAT, ARCO+, Brechas, DPIA, Documentos, CMP, Fiscalización, Equipo) debe contratar un plan.',
  },
  {
    q: '¿Los datos de mi empresa están seguros?',
    a: 'Sí. Multi-tenant estricto por organization_id, aislamiento a nivel de base de datos, bitácora inmutable de accesos, cifrado en tránsito y en reposo. Priviax se aplica Priviax: nuestro propio cumplimiento de la Ley 21.719 está publicado en la plataforma.',
  },
  {
    q: '¿Sirve para estudios jurídicos que asesoran a varios clientes?',
    a: 'Sí, con el plan Enterprise. Multi-tenant padre/hijo permite que su estudio administre N organizaciones cliente desde un solo panel, cada una con sus propios datos aislados y su propio branding si activa white-label.',
  },
  {
    q: '¿Reemplaza a mi asesor legal?',
    a: 'No. Priviax es la infraestructura operativa de cumplimiento: score, tareas, evidencia y trazabilidad. Su abogado sigue siendo el que interpreta zonas grises, firma opiniones legales y le representa ante la Agencia. Priviax hace que esa relación rinda 3× más porque llega con datos, no con dudas.',
  },
  {
    q: '¿Emiten boleta o factura electrónica?',
    a: 'Sí. Emitimos boleta o factura electrónica desde SII a nombre de su razón social con el RUT registrado. El detalle se coordina al contratar el plan escribiendo a hola@priviax.cl.',
  },
];

export default function Landing() {
  const { user } = useAuth();
  const [recentNews, setRecentNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = 'Priviax · Cumplimiento Ley 21.719 para PyMEs chilenas';
    (async () => {
      try {
        const { data } = await api.get('/news?limit=3');
        setRecentNews((data.items || []).slice(0, 3));
      } catch (_e) {
        setRecentNews([]);
      } finally {
        setNewsLoading(false);
      }
    })();
  }, []);

  const daysLeft = useMemo(() => {
    const target = new Date('2026-12-01T00:00:00-04:00');
    const diff = Math.max(0, Math.ceil((target - new Date()) / (1000 * 60 * 60 * 24)));
    return diff;
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#1C2329]" data-testid="landing-page">
      {/* ===== Announcement bar (campaña diagnóstico gratis) ===== */}
      <div className="bg-[#143A34] text-white" data-testid="landing-diag-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-[13px] sm:text-[14px] font-medium text-center sm:text-left">
            <Sparkles size={14} className="inline mr-1.5 -mt-0.5 text-[#7EE0AC]" />
            ¿Está su empresa preparada para la nueva Ley 21.719? Descúbralo en 15 minutos.
          </div>
          <Link
            to="/diagnostico-gratis"
            className="bg-[#2A9B68] hover:bg-[#238558] text-white text-[12.5px] font-medium px-4 py-1.5 rounded-sm inline-flex items-center gap-1.5 shrink-0 transition-colors"
            data-testid="landing-diag-cta-top"
          >
            Diagnóstico gratis <ArrowRight size={13} />
          </Link>
        </div>
      </div>
      {/* ===== NAV ===== */}
      <header className="border-b border-[#E5E7EB] bg-white/95 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
            <LogoFull height={28} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-[13.5px] text-[#374151]">
            <a href="#modulos" className="hover:text-[#1C2329]">Módulos</a>
            <a href="#como-funciona" className="hover:text-[#1C2329]">Cómo funciona</a>
            <a href="#planes" className="hover:text-[#1C2329]">Planes</a>
            <a href="#noticias" className="hover:text-[#1C2329]" data-testid="nav-noticias">Noticias</a>
            <a href="#faq" className="hover:text-[#1C2329]">Preguntas</a>
            <a href="#contacto" className="hover:text-[#1C2329]" data-testid="nav-contacto">Contacto</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/app/dashboard" className="pv-btn-primary flex items-center gap-2" data-testid="landing-goto-app">
                Ir al panel <ArrowRight size={14} />
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-[13.5px] text-[#374151] hover:text-[#1C2329] px-3 py-2" data-testid="landing-login">
                  Ingresar
                </Link>
                <Link to="/registro" className="pv-btn-primary flex items-center gap-2" data-testid="landing-register-nav">
                  Crear organización <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pv-hero-grid opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-[1.05fr_1fr] gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-[#E5E7EB] bg-white rounded-full px-3 py-1 text-[11.5px] text-[#374151] mb-6" data-testid="hero-countdown">
              <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] animate-pulse" />
              Faltan <b className="text-[#1C2329] tabular-nums">{daysLeft}</b> días para la entrada en vigencia · 1 dic 2026
            </div>

            <h1 className="font-display text-[52px] md:text-[68px] lg:text-[80px] leading-[0.98] text-[#1C2329]">
              ¿No sabe por dónde empezar<br/>con la <span className="text-[#143A34]">Ley 21.719</span>?
            </h1>
            <p className="mt-6 text-[17px] md:text-[18px] leading-[1.55] text-[#4B5A6B] max-w-[640px]">
              <b className="text-[#1C2329]">Priviax le dice qué hacer y le guía paso a paso para cumplir.</b> No necesita ser experto en protección de datos: hacemos el diagnóstico, le damos su plan priorizado y le acompañamos hasta la evidencia que exige la Agencia.
            </p>

            <p className="mt-3 text-[13.5px] text-[#6B7280] leading-relaxed max-w-xl">
              Multas de hasta <b>20.000 UTM (~$1.400 millones CLP)</b> o el <b>4% de los ingresos anuales</b> para infracciones gravísimas.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {user ? (
                <Link to="/app/dashboard" className="pv-btn-primary flex items-center gap-2 text-[15px] px-5 py-2.5" data-testid="landing-goto-app-hero">
                  Ir al panel <ArrowRight size={16} />
                </Link>
              ) : (
                <>
                  <Link to="/registro" className="pv-btn-primary flex items-center gap-2 text-[15px] px-5 py-2.5" data-testid="landing-cta-register">
                    Crear organización gratis <ArrowRight size={16} />
                  </Link>
                  <Link to="/login" className="pv-btn-secondary flex items-center gap-2 text-[15px] px-5 py-2.5" data-testid="landing-cta-login">
                    Ya tengo cuenta
                  </Link>
                </>
              )}
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <Stat k="12" v="Módulos operativos" />
              <Stat k="15 min" v="Diagnóstico inicial" />
              <Stat k="72 h" v="Notificación brechas" />
            </div>
          </div>

          {/* Mockup ilustrativo */}
          <div className="relative">
            <div className="absolute -inset-6 bg-[#F7F8FA] rounded-lg -z-10" />
            <div className="pv-card p-6 shadow-sm">
              <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-4">
                <div className="flex items-center gap-2">
                  <LogoMark size={20} />
                  <div className="text-[13px] font-medium">Dashboard · Cordillera Retail SpA</div>
                </div>
                <span className="pv-eyebrow">Vista real</span>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="border border-[#E5E7EB] rounded-md p-4">
                  <div className="pv-eyebrow">Score</div>
                  <div className="mt-2 text-4xl font-semibold tabular-nums text-[#1C2329]">72<span className="text-lg text-[#6B7280]">/100</span></div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] border border-[#2A9B68]/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2A9B68]" /> Bueno
                  </div>
                </div>
                <div className="border border-[#DC2626]/30 rounded-md p-4 bg-[#FEF2F2]">
                  <div className="pv-eyebrow text-[#7F1D1D]">SLA en riesgo</div>
                  <div className="mt-2 text-4xl font-semibold tabular-nums text-[#7F1D1D]">6</div>
                  <div className="mt-2 text-[11px] text-[#7F1D1D]/80">3 vencidos · 2 próximos · 1 atento</div>
                </div>
              </div>

              <div className="mt-4 border border-[#E5E7EB] rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="pv-eyebrow">Evolución del score</div>
                  <span className="text-[11px] text-[#6B7280]">Últimos 90 días</span>
                </div>
                <svg viewBox="0 0 300 70" className="w-full">
                  <defs>
                    <linearGradient id="hh" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#2A9B68" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="#2A9B68" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0 55 L40 50 L80 42 L120 46 L160 34 L200 26 L240 22 L280 18 L300 15 L300 70 L0 70 Z" fill="url(#hh)" />
                  <path d="M0 55 L40 50 L80 42 L120 46 L160 34 L200 26 L240 22 L280 18 L300 15" fill="none" stroke="#2A9B68" strokeWidth="2" />
                </svg>
              </div>

              <div className="mt-4 space-y-2">
                {[
                  { p: 'Alta', t: 'Notificar brecha 72h · pendrive extraviado', c: '#DC2626', b: '#FEF2F2' },
                  { p: 'Alta', t: 'Responder solicitud ARCO+ pendiente (2d)', c: '#DC2626', b: '#FEF2F2' },
                  { p: 'Media', t: 'Firmar DPIA · videovigilancia bodega', c: '#F59E0B', b: '#FFF7E6' },
                ].map((r) => (
                  <div key={r.t} className="flex items-center justify-between border border-[#E5E7EB] rounded-md px-3 py-2 text-[13px]">
                    <span className="text-[#1C2329]">{r.t}</span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] px-1.5 py-0.5 rounded" style={{ background: r.b, color: r.c }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.c }} /> {r.p}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MODULES ===== */}
      <section id="modulos" className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid md:grid-cols-[1fr_auto] items-end gap-6 mb-10">
            <div>
              <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05] max-w-3xl">
                Doce módulos. Pero lo importante es la ruta: le decimos qué hacer, le acompañamos mientras lo hace, y le dejamos la evidencia.
              </h2>
              <p className="mt-4 text-[15px] text-[#6B7280] max-w-2xl">
                Todo lo que la Agencia le va a pedir, resuelto de forma operativa: registro, documentos, cookies, derechos de titulares, brechas, evaluaciones de impacto, trazabilidad y evidencia empaquetada.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[12px] text-[#6B7280]">
              <span className="pv-chip"><span className="pv-chip-dot" /> Disponible en vivo</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map((m) => (
              <div
                key={m.title}
                className="p-5 rounded-md border border-[#E5E7EB] bg-white hover:border-[#2A9B68]/50 transition-colors"
                data-testid={`module-${m.title}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center bg-[#ECFDF5] text-[#2A9B68]">
                    <m.icon size={16} strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider border border-[#2A9B68]/30 text-[#065F46] bg-[#ECFDF5]">
                    Disponible
                  </span>
                </div>
                <div className="mt-4 text-[15px] font-medium text-[#1C2329]">{m.title}</div>
                <div className="mt-1 text-[13px] text-[#6B7280] leading-relaxed">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="como-funciona" className="border-t border-[#E5E7EB] bg-[#0B1220] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-white leading-[1.05]">
              De cero a plan de cumplimiento en cuatro pasos.
            </h2>
            <p className="mt-4 text-[15px] text-white/60">
              Diseñado para gerentes de operaciones, RRHH y TI. Sin conceptos legales innecesarios.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.n} className="p-6 rounded-md border border-white/10 bg-white/[0.03]" data-testid={`step-${s.n}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[13px] text-[#7EE0AC]">{s.n}</span>
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">{`Paso ${i + 1}`}</span>
                </div>
                <div className="mt-6 text-[16px] font-medium text-white">{s.t}</div>
                <div className="mt-2 text-[13.5px] text-white/60 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            {!user && (
              <Link to="/registro" className="pv-btn-primary flex items-center gap-2 text-[15px] px-5 py-2.5" data-testid="dark-cta-register">
                Comenzar mi diagnóstico <ArrowRight size={16} />
              </Link>
            )}
            {user && (
              <Link to="/app/dashboard" className="pv-btn-primary flex items-center gap-2 text-[15px] px-5 py-2.5" data-testid="dark-cta-app">
                Ir a mi panel <ArrowRight size={16} />
              </Link>
            )}
          </div>

          {/* Pilares */}
          <div className="mt-20 pt-14 border-t border-white/10">
            <div className="max-w-3xl mb-10">
              <h3 className="text-[24px] md:text-[28px] font-semibold tracking-[-0.02em] text-white leading-[1.15]">
                Diseñado para quienes cumplen la ley, no para quienes la escriben.
              </h3>
              <p className="mt-3 text-[14px] text-white/60">
                Cuatro decisiones de producto que hacen Priviax distinto de cualquier suite tradicional de compliance.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {PILLARS.map((p) => (
                <div key={p.title} className="p-5 rounded-md border border-white/10 bg-white/[0.03]" data-testid={`pillar-${p.title}`}>
                  <p.icon size={18} className="text-[#7EE0AC]" strokeWidth={1.5} />
                  <div className="mt-4 text-[15px] font-medium text-white">{p.title}</div>
                  <div className="mt-1 text-[13.5px] text-white/60 leading-relaxed">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS — oculto hasta tener opiniones reales ===== */}
      {false && (
      <section id="clientes" className="border-t border-[#E5E7EB] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl mb-10">
            <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05]">
              Lo que dicen quienes ya llegaron preparados.
            </h2>
            <p className="mt-4 text-[15px] text-[#6B7280]">
              Empresas medianas chilenas y estudios jurídicos que usan Priviax en producción.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.name}
                className="p-6 border border-[#E5E7EB] bg-[#FAFBFC] flex flex-col"
                data-testid={`testimonial-${t.name}`}
              >
                <div className="text-[#2A9B68] font-display text-[36px] leading-none">&ldquo;</div>
                <blockquote className="mt-2 text-[14.5px] text-[#1C2329] leading-relaxed flex-1">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 pt-4 border-t border-[#E5E7EB]">
                  <div className="text-[13.5px] font-medium text-[#1C2329]">{t.name}</div>
                  <div className="text-[12px] text-[#6B7280] mt-0.5">{t.role} · {t.org}</div>
                  <div className="text-[11px] text-[#9CA3AF] mt-1 uppercase tracking-wider">{t.sector}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ===== LAW BAND ===== */}
      <section id="ley" className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05]">
              Nueva normativa chilena de protección de datos personales.
            </h2>
            <p className="mt-5 text-[15px] text-[#374151] leading-relaxed">
              La Ley 21.719 crea la <b>Agencia de Protección de Datos Personales</b> y establece obligaciones concretas: consentimiento auditable, derechos ARCO+, notificación de brechas en 72 horas, evaluaciones de impacto (DPIA) y sanciones que pueden llegar al 4% de los ingresos anuales.
            </p>
            <p className="mt-3 text-[15px] text-[#374151] leading-relaxed">
              Priviax le ayuda a llegar preparado al <span className="font-medium text-[#1C2329]">1 de diciembre de 2026</span> con evidencia lista para fiscalizaciones — no con un PowerPoint.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { k: '4%', v: 'Multas máximas sobre ingresos anuales' },
              { k: '72 h', v: 'Plazo para notificar brechas a la Agencia' },
              { k: '15 días hábiles', v: 'Plazo para responder solicitudes ARCO+' },
              { k: '1 dic 2026', v: 'Entrada en vigencia plena' },
            ].map((s) => (
              <div key={s.k} className="p-5 rounded-md border border-[#E5E7EB] bg-white">
                <div className="text-3xl font-semibold text-[#143A34] tracking-tight tabular-nums">{s.k}</div>
                <div className="mt-1 text-[13px] text-[#6B7280]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PLANS ===== */}
      <section id="planes" className="border-t border-[#E5E7EB] bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05]">
              Precios claros en UF.
            </h2>
            <p className="mt-4 text-[15px] text-[#6B7280]">
              Tres planes con la <b>funcionalidad completa</b> — diferenciamos por capacidad (titulares, RATs, usuarios, dominios) y SLA de soporte, no por features. Facturación con boleta o factura electrónica desde SII.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {PLANS.map((p) => (
              <div
                key={p.key}
                className={`flex flex-col p-6 border ${p.featured ? 'border-[#2A9B68] bg-white shadow-md relative' : 'border-[#E5E7EB] bg-white'}`}
                data-testid={`plan-${p.key}`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-6 text-[10px] px-2 py-1 rounded-full bg-[#143A34] text-white uppercase tracking-wider font-semibold">
                    {p.badge}
                  </div>
                )}
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">{p.size}</div>
                <div className="mt-2 text-[20px] font-semibold text-[#1C2329]">{p.name}</div>
                <div className="mt-1 text-[13px] text-[#6B7280] leading-snug min-h-[38px]">{p.tagline}</div>

                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-[32px] font-semibold text-[#1C2329] tracking-tight tabular-nums">{p.price}</span>
                </div>
                <div className="text-[11.5px] text-[#6B7280]">{p.per}</div>

                {p.key === 'enterprise' ? (
                  <a
                    href="#contacto"
                    className={`mt-5 block w-full text-center px-4 py-2.5 text-[13px] font-medium border transition-colors ${
                      p.featured
                        ? 'bg-[#143A34] hover:bg-[#0F2A26] text-white border-[#143A34]'
                        : 'bg-white hover:bg-[#F7F8FA] text-[#1C2329] border-[#D1D5DB]'
                    }`}
                    data-testid={`plan-cta-${p.key}`}
                  >
                    {p.cta}
                  </a>
                ) : (
                  <Link
                    to="/registro"
                    className={`mt-5 block w-full text-center px-4 py-2.5 text-[13px] font-medium border transition-colors ${
                      p.featured
                        ? 'bg-[#143A34] hover:bg-[#0F2A26] text-white border-[#143A34]'
                        : 'bg-white hover:bg-[#F7F8FA] text-[#1C2329] border-[#D1D5DB]'
                    }`}
                    data-testid={`plan-cta-${p.key}`}
                  >
                    {p.cta}
                  </Link>
                )}

                <ul className="mt-6 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px] text-[#374151]">
                      <Check size={13} className="mt-1 text-[#2A9B68] shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                  {p.excluded.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12.5px] text-[#9CA3AF]">
                      <Minus size={13} className="mt-1 shrink-0" />
                      <span className="line-through">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-4 text-[13px] text-[#374151]">
            <div className="p-4 border border-[#E5E7EB] bg-white">
              <div className="font-medium text-[#1C2329]">Diagnóstico gratis para siempre</div>
              <div className="text-[12.5px] text-[#6B7280] mt-1">Puede probar el diagnóstico y ver su score sin pagar. Los pagos se activan solo al habilitar módulos avanzados.</div>
            </div>
            <div className="p-4 border border-[#E5E7EB] bg-white">
              <div className="font-medium text-[#1C2329]">Cambio de plan sin fricción</div>
              <div className="text-[12.5px] text-[#6B7280] mt-1">Suba o baje su plan mes a mes. Prorrateo automático. Cancela cuando quiera sin costo.</div>
            </div>
            <div className="p-4 border border-[#E5E7EB] bg-white">
              <div className="font-medium text-[#1C2329]">Descuento anual · 15%</div>
              <div className="text-[12.5px] text-[#6B7280] mt-1">Pagando anual obtiene un mes y medio gratis. Business y Enterprise incluyen success manager.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== NEWS PREVIEW ===== */}
      <section id="noticias" className="border-t border-[#E5E7EB] bg-white" data-testid="landing-news">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid md:grid-cols-[1fr_auto] items-end gap-6 mb-10">
            <div>
              <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05] max-w-3xl">
                Últimas noticias sobre protección de datos en Chile.
              </h2>
              <p className="mt-3 text-[14px] text-[#6B7280] max-w-2xl">
                Curamos automáticamente noticias de BioBíoChile, La Tercera, Diario Financiero, Emol, CIPER y Cooperativa.
              </p>
            </div>
            <Link
              to="/noticias"
              className="pv-btn-secondary hidden md:inline-flex items-center gap-2 text-[13.5px]"
              data-testid="landing-news-view-all"
            >
              Ver todas las noticias <ArrowRight size={14} />
            </Link>
          </div>

          {newsLoading ? (
            <div className="text-sm text-[#6B7280]">Cargando noticias…</div>
          ) : recentNews.length === 0 ? (
            <div className="pv-card p-8 text-center text-sm text-[#6B7280]">
              Aún no hay noticias cargadas. Estamos consultando los feeds; vuelva en unos minutos.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {recentNews.map((n) => (
                <article
                  key={n.id}
                  className="pv-card p-5 flex flex-col hover:border-[#2A9B68]/50 transition-colors"
                  data-testid={`landing-news-card-${n.id}`}
                >
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-[#143A34] font-medium">{n.source}</span>
                    <span className="text-[#6B7280]">{formatDate(n.published_at)}</span>
                  </div>
                  <a
                    href={n.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 text-[15.5px] font-medium text-[#1C2329] leading-snug hover:text-[#143A34] line-clamp-3 flex-1"
                    data-testid={`landing-news-link-${n.id}`}
                  >
                    {n.title}
                  </a>
                  {n.keywords_matched?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {n.keywords_matched.slice(0, 2).map((kw) => (
                        <span key={kw} className="text-[10.5px] px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB]">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                  <a
                    href={n.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 pt-3 border-t border-[#E5E7EB] text-[12.5px] text-[#2A9B68] hover:text-[#228256] inline-flex items-center gap-1"
                  >
                    Leer en {n.source} <ExternalLink size={12} />
                  </a>
                </article>
              ))}
            </div>
          )}

          <div className="mt-8 md:hidden">
            <Link
              to="/noticias"
              className="pv-btn-secondary inline-flex items-center gap-2 text-[13.5px] w-full justify-center"
              data-testid="landing-news-view-all-mobile"
            >
              Ver todas las noticias <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="border-t border-[#E5E7EB] bg-[#FAFBFC]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
          <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05]">
            Preguntas frecuentes.
          </h2>
          <p className="mt-4 text-[15px] text-[#6B7280]">
            Las 7 dudas que aparecen en todos los primeros meetings.
          </p>

          <div className="mt-10 space-y-2">
            {FAQS.map((f, i) => (
              <div
                key={f.q}
                className="border border-[#E5E7EB] bg-white"
                data-testid={`faq-${i}`}
              >
                <button
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#FAFBFC]"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="text-[15px] font-medium text-[#1C2329]">{f.q}</span>
                  <ChevronDown
                    size={16}
                    className={`text-[#6B7280] transition-transform shrink-0 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-[14px] text-[#374151] leading-relaxed border-t border-[#E5E7EB] pt-4">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA + CONTACTO ===== */}
      <section id="contacto" className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-[34px] md:text-[42px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05]">
              Su primer score de cumplimiento en 15 minutos.
            </h2>
            <p className="mt-5 text-[15px] text-[#6B7280] max-w-xl">
              Cree su organización, responda el diagnóstico guiado y reciba su score de cumplimiento gratis. El plan de acción y los módulos operativos se desbloquean al contratar un plan.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <Link to="/app/dashboard" className="pv-btn-primary flex items-center gap-2 text-[15px] px-5 py-2.5">
                  Ir al panel <ArrowRight size={16} />
                </Link>
              ) : (
                <Link to="/registro" className="pv-btn-primary flex items-center gap-2 text-[15px] px-5 py-2.5" data-testid="footer-cta-register">
                  Crear organización gratis <ArrowRight size={16} />
                </Link>
              )}
            </div>
            <p className="mt-10 text-[13px] text-[#6B7280] leading-relaxed max-w-md">
              ¿Le interesa el plan <b className="text-[#1C2329]">Enterprise</b> o quiere agendar una reunión?
              Déjenos sus datos en el formulario y le contactamos a la brevedad.
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-[#E5E7EB] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid md:grid-cols-4 gap-8 text-[13px] text-[#6B7280]">
          <div>
            <LogoFull height={26} />
            <p className="mt-4 leading-relaxed">
              Cumplimiento operativo de la Ley N.º 21.719 para empresas chilenas.
            </p>
            <p className="mt-3 text-[11.5px] text-[#9CA3AF]">
              Santiago, Chile · © {new Date().getFullYear()} Priviax SpA
            </p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#1C2329] font-semibold mb-3">Producto</div>
            <ul className="space-y-2">
              <li><a href="#modulos" className="hover:text-[#1C2329]">Módulos</a></li>
              <li><a href="#como-funciona" className="hover:text-[#1C2329]">Cómo funciona</a></li>
              <li><a href="#planes" className="hover:text-[#1C2329]">Planes y precios</a></li>
              <li><a href="#faq" className="hover:text-[#1C2329]">Preguntas frecuentes</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#1C2329] font-semibold mb-3">Recursos</div>
            <ul className="space-y-2">
              <li><Link to="/noticias" className="hover:text-[#1C2329]">Noticias Ley 21.719</Link></li>
              <li><a href="#ley" className="hover:text-[#1C2329]">Sobre la ley</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#1C2329] font-semibold mb-3">Legal</div>
            <ul className="space-y-2">
              <li><Link to="/privacidad" className="hover:text-[#1C2329]">Política de Privacidad</Link></li>
              <li><Link to="/terminos" className="hover:text-[#1C2329]">Términos y Condiciones</Link></li>
              <li><Link to="/cookies-y-almacenamiento" className="hover:text-[#1C2329]">Cookies y almacenamiento</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#1C2329] font-semibold mb-3">Cuenta</div>
            <ul className="space-y-2">
              <li><Link to="/login" className="hover:text-[#1C2329]" data-testid="footer-login">Ingresar</Link></li>
              <li><Link to="/registro" className="hover:text-[#1C2329]">Crear organización</Link></li>
              <li><a href="#contacto" className="hover:text-[#1C2329]">Formulario de contacto</a></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="text-2xl font-semibold text-[#1C2329] tabular-nums">{k}</div>
      <div className="text-[12px] text-[#6B7280] mt-0.5">{v}</div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ nombre: '', email: '', empresa: '', telefono: '', mensaje: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/public/contact', form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos enviar su mensaje. Intente nuevamente.');
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="pv-card p-8 text-center" data-testid="contact-success">
        <div className="w-11 h-11 mx-auto rounded-full bg-[#ECFDF5] flex items-center justify-center">
          <Check size={20} className="text-[#065F46]" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-[#1C2329]">Mensaje enviado</h3>
        <p className="mt-2 text-[14px] text-[#6B7280] leading-relaxed">
          Gracias por su interés. Le contactaremos a la brevedad al correo indicado.
        </p>
      </div>
    );
  }

  return (
    <div className="pv-card p-6 md:p-8" data-testid="contact-form-card">
      <h3 className="text-lg font-semibold text-[#1C2329]">Hablemos</h3>
      <p className="text-[13px] text-[#6B7280] mt-1">Cuéntenos qué necesita y le respondemos dentro del día hábil.</p>
      <form onSubmit={submit} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="contact-form">
        <div>
          <label className="block text-[11.5px] font-medium text-[#374151] mb-1 uppercase tracking-wider">Nombre *</label>
          <input required className="pv-input" value={form.nombre} onChange={set('nombre')} data-testid="contact-nombre" />
        </div>
        <div>
          <label className="block text-[11.5px] font-medium text-[#374151] mb-1 uppercase tracking-wider">Correo *</label>
          <input required type="email" className="pv-input" value={form.email} onChange={set('email')} data-testid="contact-email" />
        </div>
        <div>
          <label className="block text-[11.5px] font-medium text-[#374151] mb-1 uppercase tracking-wider">Empresa</label>
          <input className="pv-input" value={form.empresa} onChange={set('empresa')} data-testid="contact-empresa" />
        </div>
        <div>
          <label className="block text-[11.5px] font-medium text-[#374151] mb-1 uppercase tracking-wider">Teléfono</label>
          <input className="pv-input" value={form.telefono} onChange={set('telefono')} placeholder="+56 9 …" data-testid="contact-telefono" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11.5px] font-medium text-[#374151] mb-1 uppercase tracking-wider">Mensaje *</label>
          <textarea
            required rows={4} className="pv-input resize-none"
            value={form.mensaje} onChange={set('mensaje')}
            placeholder="Ej.: Nos interesa evaluar el plan Enterprise para un holding de 5 empresas…"
            data-testid="contact-mensaje"
          />
        </div>
        <div className="sm:col-span-2 flex justify-end pt-1">
          <button
            type="submit" disabled={sending}
            className="pv-btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="contact-submit"
          >
            {sending ? 'Enviando…' : 'Enviar'} <ArrowRight size={15} />
          </button>
        </div>
      </form>
    </div>
  );
}
